/**
 * ExternalPlatformLink
 *
 * Button + confirmation dialog for links to external platforms (e.g. BookingLayer).
 * Shows a confirmation before opening in a new tab so the user is never surprised.
 *
 * Usage:
 *   <ExternalPlatformLink platform="BookingLayer" url={blUrl} label="2026-0001" />
 */

import { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { useTranslation } from '@/core';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/theme/components/ui/alert-dialog';

export interface ExternalPlatformLinkProps {
  /** Human-readable platform name shown in the dialog, e.g. "BookingLayer" */
  platform: string;
  /** Full URL to open */
  url: string;
  /** Text shown as the clickable label */
  label: string;
  /** Additional CSS classes for the trigger button */
  className?: string;
}

export function ExternalPlatformLink({ platform, url, label, className }: ExternalPlatformLinkProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={
          className ??
          'inline-flex items-center gap-1 text-sm text-primary hover:underline font-medium'
        }
      >
        {label}
        <ExternalLink className="h-3 w-3" />
      </button>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('common.openInPlatformTitle', { platform })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('common.openInPlatformDesc', { platform })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              {t('common.openInNewTab')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
